import { X } from "@icons";
import { useAnnounce } from "@src/components/live-announcer";
import { focusMain } from "@src/utils/focus-main";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CloseButtonProps, Id, ToastContainer, toast } from "react-toastify";

const REGION_SELECTOR = ".Toastify";
const TOAST_SELECTOR = ".Toastify__toast";
const DISMISS_SELECTOR = ".Toastify__close-button";
const CONTAINER_BOTTOM_OFFSET = "4.5rem";
const MAX_VISIBLE_TOASTS = 3;

interface VisibleToast {
  id: Id;
  isError: boolean;
}

let returnTarget: HTMLElement | null = null;
const dismissedButtons = new WeakSet<HTMLElement>();

function activeDismissButtons(): HTMLElement[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>(
      `${REGION_SELECTOR} ${TOAST_SELECTOR} ${DISMISS_SELECTOR}`,
    ),
  ).filter((button) => !dismissedButtons.has(button));
}

function focusAfterDismiss(dismissed: HTMLElement) {
  const owner = dismissed.closest(TOAST_SELECTOR);
  if (!owner?.contains(document.activeElement)) {
    dismissedButtons.add(dismissed);
    return;
  }
  const active = activeDismissButtons();
  const index = active.indexOf(dismissed);
  dismissedButtons.add(dismissed);
  const sibling = active[index + 1] ?? active[index - 1];
  if (sibling) {
    sibling.focus();
    return;
  }
  const target =
    returnTarget && document.contains(returnTarget) ? returnTarget : null;
  returnTarget = null;
  if (target) {
    target.focus();
    return;
  }
  focusMain();
}

function ToastCloseButton({ closeToast }: CloseButtonProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className={`Toastify__close-button flex !text-muted !opacity-100 min-h-[44px] min-w-[44px] items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus`}
      aria-label={t("dismissNotification")}
      onClick={(event) => {
        event.stopPropagation();
        focusAfterDismiss(event.currentTarget);
        closeToast(true);
      }}
    >
      <X size={14} aria-hidden="true" />
    </button>
  );
}

export default function ToastRegion() {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const visible = useRef<VisibleToast[]>([]);
  const [hasToasts, setHasToasts] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    wrapper.querySelector(REGION_SELECTOR)?.setAttribute("aria-live", "off");

    const removeToastTabStops = () => {
      wrapper
        .querySelectorAll<HTMLElement>(TOAST_SELECTOR)
        .forEach((element) => element.setAttribute("tabindex", "-1"));
    };
    removeToastTabStops();
    const observer = new MutationObserver(removeToastTabStops);
    observer.observe(wrapper, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const unsubscribe = toast.onChange((item) => {
      if (item.status === "removed") {
        visible.current = visible.current.filter(({ id }) => id !== item.id);
        setHasToasts(visible.current.length > 0);
        return;
      }
      if (item.status !== "added") return;
      const isError = item.type === "error";
      visible.current.push({ id: item.id, isError });
      setHasToasts(true);
      if (typeof item.content === "string") {
        announce(item.content, isError ? "assertive" : "polite");
      }
      if (visible.current.length > MAX_VISIBLE_TOASTS) {
        const older = visible.current.filter(({ id }) => id !== item.id);
        const evicted = older.find(({ isError }) => !isError) ?? older[0];
        toast.dismiss(evicted.id);
      }
    });
    return unsubscribe;
  }, [announce]);

  useEffect(() => {
    const isInRegion = (node: EventTarget | null) =>
      node instanceof Element && node.closest(REGION_SELECTOR) !== null;

    const handleFocusIn = (event: FocusEvent) => {
      if (!isInRegion(event.target) || isInRegion(event.relatedTarget)) return;
      const from = event.relatedTarget;
      returnTarget =
        from instanceof HTMLElement && from !== document.body ? from : null;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !isInRegion(event.target)) return;
      const toastElement = (event.target as Element).closest(TOAST_SELECTOR);
      toastElement?.querySelector<HTMLElement>(DISMISS_SELECTOR)?.click();
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const containerProps = {
    role: "none",
    "aria-label": hasToasts ? t("notificationsRegion") : undefined,
  };

  return (
    <div ref={wrapperRef} className="contents">
      <ToastContainer
        autoClose={false}
        position="bottom-right"
        style={{ bottom: CONTAINER_BOTTOM_OFFSET }}
        hotKeys={() => false}
        {...containerProps}
        closeButton={(props) => <ToastCloseButton {...props} />}
      />
    </div>
  );
}
